export const OWNER_TEAM_NAME = 'Owner';
export const SYSTEM_GROUP_NAME = 'System';

const OWNER_LABEL = '(platform owner — do not modify)';

export function isOwnerTeamName(name) {
  return String(name || '').trim().toLowerCase() === OWNER_TEAM_NAME.toLowerCase();
}

function parentGroupIsSystem(parentType, parentName) {
  const type = String(parentType || '').toLowerCase();
  const name = String(parentName || '').trim();
  return type === 'system' || name.toLowerCase() === SYSTEM_GROUP_NAME.toLowerCase();
}

/** Parent org label from UGP/role row (org root via superparent, then immediate parent). */
export function resolveParentGroupLabel(row) {
  if (!row) return '';
  const orgRoot = String(row.organization_group_name || '').trim();
  if (orgRoot) return orgRoot;
  const orgTitle = String(row.organization_groups_periods_title || '').trim();
  if (orgTitle) {
    const dash = orgTitle.indexOf(' - ');
    return dash >= 0 ? orgTitle.slice(0, dash).trim() : orgTitle;
  }
  const direct = String(row.parent_group_name || '').trim();
  if (direct) return direct;
  const fromTitle = String(row.parent_groups_periods_title || '').trim();
  if (!fromTitle) return '';
  const dash = fromTitle.indexOf(' - ');
  return dash >= 0 ? fromTitle.slice(0, dash).trim() : fromTitle;
}

/** Team label from UGP/role row (matches Navigation group context). */
export function resolveRoleGroupLabel(row) {
  if (!row) return '';
  return String(row.group_name || row.groups_periods_title || '').trim();
}

/**
 * Same rule as nav context: team Owner under parent System (group_type system on parent).
 * Uses fields already on AuthContext currentRole / availableUgp.
 */
export function authRoleIsPlatformOwner(role) {
  if (!role) return false;
  if (!isOwnerTeamName(resolveRoleGroupLabel(role))) return false;
  const parentLabel = resolveParentGroupLabel(role);
  if (parentGroupIsSystem(role.parent_group_type, parentLabel)) return true;
  return parentLabel.toLowerCase() === SYSTEM_GROUP_NAME.toLowerCase();
}

export function buildGroupsPeriodsById(groupsPeriods = []) {
  return new Map((groupsPeriods || []).map((gp) => [String(gp.id), gp]));
}

function parentIsCustomerOrganization(parentType, parentName, groupsPeriodsById, parentGpId) {
  const type = String(parentType || '').toLowerCase();
  if (type === 'organization' || type === 'school') return true;
  if (parentGpId && groupsPeriodsById) {
    const parent = groupsPeriodsById.get(String(parentGpId));
    const pType = String(parent?.group_type || '').toLowerCase();
    if (pType === 'organization' || pType === 'school') {
      const pName = String(parent?.group_name || '').trim();
      return pName.toLowerCase() !== SYSTEM_GROUP_NAME.toLowerCase();
    }
  }
  const name = String(parentName || '').trim();
  return (
    name.length > 0 && name.toLowerCase() !== SYSTEM_GROUP_NAME.toLowerCase() && type !== 'system'
  );
}

/** Owner team GP under a customer organization (not System → Owner). */
export function isOrgOwnerGp(gp, hierarchyMeta = {}, groupsPeriodsById = null) {
  if (!gp || !isOwnerTeamName(gp.group_name)) return false;
  const teamType = String(gp.group_type || 'team').toLowerCase();
  if (teamType && teamType !== 'team') return false;
  if (isSystemOwnerGp(gp, hierarchyMeta, groupsPeriodsById)) return false;

  const byId =
    groupsPeriodsById instanceof Map
      ? groupsPeriodsById
      : Array.isArray(groupsPeriodsById)
        ? buildGroupsPeriodsById(groupsPeriodsById)
        : null;

  if (parentIsCustomerOrganization(gp.parent_group_type, gp.parent_group_name, byId, null)) {
    return true;
  }

  const parentGpId = hierarchyMeta[String(gp.id)]?.parentGpId;
  if (parentGpId && byId) {
    const parent = byId.get(String(parentGpId));
    if (parentIsCustomerOrganization(parent?.group_type, parent?.group_name, byId, parentGpId)) {
      return true;
    }
  }
  return false;
}

/** Owner team GP nested under the System group (parent group_type system). */
export function isSystemOwnerGp(gp, hierarchyMeta = {}, groupsPeriodsById = null) {
  if (!gp || !isOwnerTeamName(gp.group_name)) return false;
  const teamType = String(gp.group_type || 'team').toLowerCase();
  if (teamType && teamType !== 'team') return false;

  if (parentGroupIsSystem(gp.parent_group_type, gp.parent_group_name)) return true;

  const byId =
    groupsPeriodsById instanceof Map
      ? groupsPeriodsById
      : Array.isArray(groupsPeriodsById)
        ? buildGroupsPeriodsById(groupsPeriodsById)
        : null;

  const parentGpId = hierarchyMeta[String(gp.id)]?.parentGpId;
  if (parentGpId && byId) {
    const parent = byId.get(String(parentGpId));
    if (parentGroupIsSystem(parent?.group_type, parent?.group_name)) return true;
  }

  return false;
}

/** Active enrollment on Owner team under System. */
export function isSystemOwnerUgp(ugp) {
  if (!ugp) return false;
  if (authRoleIsPlatformOwner(ugp)) return true;
  if (!isOwnerTeamName(resolveRoleGroupLabel(ugp))) return false;
  const teamType = String(ugp.group_type || 'team').toLowerCase();
  if (teamType && teamType !== 'team') return false;
  return parentGroupIsSystem(ugp.parent_group_type, resolveParentGroupLabel(ugp));
}

export function collectSystemOwnerContext(
  groupsPeriods = [],
  hierarchyMeta = {},
  allUserGroupsPeriods = []
) {
  const byId = buildGroupsPeriodsById(groupsPeriods);
  const ownerGpIds = new Set();
  (groupsPeriods || []).forEach((gp) => {
    if (isSystemOwnerGp(gp, hierarchyMeta, byId)) {
      ownerGpIds.add(String(gp.id));
    }
  });

  const ownerUgpIds = new Set();
  const ownerUserIds = new Set();
  (allUserGroupsPeriods || []).forEach((ugp) => {
    const gpId = String(ugp.groups_periods_id || ugp.groups_periods_id_full || '');
    const isOwnerEnrollment = isSystemOwnerUgp(ugp) || (gpId && ownerGpIds.has(gpId));
    if (!isOwnerEnrollment) return;
    if (ugp.id) ownerUgpIds.add(String(ugp.id));
    if (ugp.user_id) ownerUserIds.add(String(ugp.user_id));
  });

  return { ownerUserIds, ownerUgpIds, ownerGpIds };
}

export function collectOrgOwnerContext(
  groupsPeriods = [],
  hierarchyMeta = {},
  allUserGroupsPeriods = []
) {
  const byId = buildGroupsPeriodsById(groupsPeriods);
  const orgOwnerGpIds = new Set();
  (groupsPeriods || []).forEach((gp) => {
    if (isOrgOwnerGp(gp, hierarchyMeta, byId)) {
      orgOwnerGpIds.add(String(gp.id));
    }
  });

  const orgOwnerUgpIds = new Set();
  const orgOwnerUserIds = new Set();
  (allUserGroupsPeriods || []).forEach((ugp) => {
    const gpId = String(ugp.groups_periods_id || ugp.groups_periods_id_full || '');
    if (!gpId || !orgOwnerGpIds.has(gpId)) return;
    if (ugp.id) orgOwnerUgpIds.add(String(ugp.id));
    if (ugp.user_id) orgOwnerUserIds.add(String(ugp.user_id));
  });

  return { orgOwnerUserIds, orgOwnerUgpIds, orgOwnerGpIds };
}

/** Platform + organization owner grantees (e.g. password guards). PPM uses {@link collectSystemOwnerContext} only. */
export function collectProtectedOwnerContext(
  groupsPeriods = [],
  hierarchyMeta = {},
  allUserGroupsPeriods = []
) {
  const platform = collectSystemOwnerContext(
    groupsPeriods,
    hierarchyMeta,
    allUserGroupsPeriods
  );
  const org = collectOrgOwnerContext(groupsPeriods, hierarchyMeta, allUserGroupsPeriods);
  return {
    ownerUserIds: new Set([...platform.ownerUserIds, ...org.orgOwnerUserIds]),
    ownerUgpIds: new Set([...platform.ownerUgpIds, ...org.orgOwnerUgpIds]),
    ownerGpIds: new Set([...platform.ownerGpIds, ...org.orgOwnerGpIds]),
    platformOwnerUserIds: platform.ownerUserIds,
    orgOwnerUserIds: org.orgOwnerUserIds
  };
}

export function userIsSystemOwnerMember(userId, enrollments = [], currentRole = null) {
  if (!userId) return false;
  if (authRoleIsPlatformOwner(currentRole)) {
    if (currentRole.user_id == null || String(currentRole.user_id) === String(userId)) {
      return true;
    }
  }
  return (enrollments || []).some((ugp) => {
    if (ugp.user_id != null && String(ugp.user_id) !== String(userId)) return false;
    return isSystemOwnerUgp(ugp);
  });
}

/** Whether the signed-in user may edit platform Owner permissions/grantees. */
export function viewerIsPlatformOwner({ userId, currentRole, enrollments = [], ownerUserIds }) {
  if (!userId) return false;
  if (authRoleIsPlatformOwner(currentRole)) return true;
  if (ownerUserIds && ownerUserIds.has(String(userId))) return true;
  return userIsSystemOwnerMember(userId, enrollments, currentRole);
}

export function isProtectedOwnerUserId(userId, ownerUserIds) {
  return ownerUserIds.has(String(userId));
}

export function isProtectedOwnerUgp(ugp, ownerUgpIds, ownerGpIds) {
  if (!ugp) return false;
  if (ownerUgpIds.has(String(ugp.id))) return true;
  const gpId = String(ugp.groups_periods_id || ugp.groups_periods_id_full || '');
  if (gpId && ownerGpIds.has(gpId)) return true;
  return isSystemOwnerUgp(ugp);
}

/** Platform System → Owner team only (not per-organization Owner teams). */
export function isProtectedOwnerGp(gp, ownerGpIds, hierarchyMeta, groupsPeriodsById = null) {
  if (!gp) return false;
  if (ownerGpIds.has(String(gp.id))) return true;
  return isSystemOwnerGp(gp, hierarchyMeta, groupsPeriodsById);
}

export function permissionTouchesOwnerGranteesFromLists(
  perm,
  { ownerUserIds, ownerUgpIds, ownerGpIds },
  groupsPeriods = [],
  hierarchyMeta = {},
  groupsPeriodsById = null
) {
  if (!perm) return false;
  const grantees = (perm.grantees || []).map(String);
  const byId =
    groupsPeriodsById instanceof Map
      ? groupsPeriodsById
      : buildGroupsPeriodsById(groupsPeriods);

  if (perm.grantee_type === 'users') {
    return grantees.some((id) => ownerUserIds.has(id));
  }
  if (perm.grantee_type === 'groups_periods') {
    return grantees.some((id) => {
      if (ownerGpIds.has(id)) return true;
      const gp = groupsPeriods.find((row) => String(row.id) === id);
      return gp ? isProtectedOwnerGp(gp, ownerGpIds, hierarchyMeta, byId) : false;
    });
  }
  if (perm.grantee_type === 'user_groups_periods') {
    return grantees.some((id) => ownerUgpIds.has(id));
  }
  return false;
}

export function ownerGranteeSuffix(isProtected) {
  return isProtected ? ` ${OWNER_LABEL}` : '';
}

export { OWNER_LABEL };
